import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { addDays, format } from "date-fns";

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(n || 0);

export default function ManualTimesheetCreator() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);

  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedWeekStart, setSelectedWeekStart] = useState(
    (() => {
      const today = new Date();
      const day = today.getDay();
      const diff = (day === 0 ? -6 : 1) - day;
      const monday = new Date(today);
      monday.setDate(today.getDate() + diff);
      return format(monday, "yyyy-MM-dd");
    })()
  );

  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState("Pending");
  const [hasExisting, setHasExisting] = useState(false);

  const [weekEnd, setWeekEnd] = useState(
    format(addDays(new Date(), 6), "yyyy-MM-dd")
  );

  // ⭐ TAX FIELDS (Per Timesheet — per your request)
  const [taxCode, setTaxCode] = useState("1257L");
  const [niCategory, setNiCategory] = useState("A");
  const [studentLoan, setStudentLoan] = useState("None");

  // Selected staff object
  const selectedStaff = useMemo(
    () => staff.find((s) => s.id === selectedStaffId),
    [staff, selectedStaffId]
  );

  // ──────────────────────────────────────────────
  // Load Staff & Clients
  // ──────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [staffSnap, clientsSnap] = await Promise.all([
          getDocs(collection(db, "staff")),
          getDocs(collection(db, "clients")),
        ]);

        setStaff(staffSnap.docs.map((d) => ({ id: d.id, ...d.data() })) || []);
        setClients(
          clientsSnap.docs.map((d) => ({ id: d.id, ...d.data() })) || []
        );
      } catch (err) {
        console.error("Error loading staff/clients:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // ──────────────────────────────────────────────
  // Load Timesheet for selected staff + week
  // ──────────────────────────────────────────────
  useEffect(() => {
    const loadTimesheet = async () => {
      if (!selectedStaffId || !selectedWeekStart) {
        setEntries([]);
        setStatus("Pending");
        setHasExisting(false);
        return;
      }

      try {
        setLoading(true);

        const we = format(addDays(new Date(selectedWeekStart), 6), "yyyy-MM-dd");
        setWeekEnd(we);

        const tsId = `${selectedStaffId}_week_${selectedWeekStart}`;
        const ref = doc(db, "timesheets", tsId);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const data = snap.data();
          setEntries(data.entries || []);
          setStatus(data.status || "Pending");
          setTaxCode(data.taxCode || "1257L");
          setNiCategory(data.niCategory || "A");
          setStudentLoan(data.studentLoan || "None");
          setHasExisting(true);

          if (data.weekEnd) {
            setWeekEnd(data.weekEnd);
          }
        } else {
          setEntries([]);
          setStatus("Pending");
          setTaxCode("1257L");
          setNiCategory("A");
          setStudentLoan("None");
          setHasExisting(false);
        }
      } catch (err) {
        console.error("Error loading timesheet:", err);
      } finally {
        setLoading(false);
      }
    };

    loadTimesheet();
  }, [selectedStaffId, selectedWeekStart]);

  // ──────────────────────────────────────────────
  // Entries
  // ──────────────────────────────────────────────
  const addEntry = () => {
    if (!selectedStaffId) return alert("Select a staff member first.");

    const defaultDate = selectedWeekStart;

    setEntries((prev) => [
      ...prev,
      {
        date: defaultDate,
        clientName: "",
        hours: 0,
        rate: selectedStaff?.rate || 15,
        approved: false,
        status: "Assigned",
      },
    ]);
  };

  const updateEntry = (i, field, value) => {
    setEntries((prev) =>
      prev.map((e, idx) => (i === idx ? { ...e, [field]: value } : e))
    );
  };

  const removeEntry = (i) => {
    if (!window.confirm("Remove this entry?")) return;
    setEntries((prev) => prev.filter((_, idx) => idx !== i));
  };

  const totals = useMemo(() => {
    const totalHours = entries.reduce(
      (sum, e) => sum + Number(e.hours || 0),
      0
    );
    const totalPay = entries.reduce(
      (sum, e) => sum + Number(e.hours || 0) * Number(e.rate || 0),
      0
    );
    const approvedGross = entries
      .filter((e) => e.approved)
      .reduce(
        (sum, e) => sum + Number(e.hours || 0) * Number(e.rate || 0),
        0
      );

    return { totalHours, totalPay, approvedGross };
  }, [entries]);

  // ──────────────────────────────────────────────
  // Save Timesheet
  // ──────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedStaffId) return alert("Select staff member.");
    if (!selectedWeekStart) return alert("Select week start.");

    try {
      setSaving(true);

      const tsId = `${selectedStaffId}_week_${selectedWeekStart}`;
      const ref = doc(db, "timesheets", tsId);

      const payload = {
        staffId: selectedStaffId,
        staffName: selectedStaff?.fullName || selectedStaff?.name || "",
        weekStart: selectedWeekStart,
        weekEnd,

        entries,
        totalHours: totals.totalHours,
        totalPay: totals.totalPay,
        approvedGross: totals.approvedGross,

        // ⭐ Save tax fields here
        taxCode,
        niCategory,
        studentLoan,

        status,
        updatedAt: serverTimestamp(),
      };

      if (!hasExisting) {
        payload.createdAt = serverTimestamp();
      }

      await setDoc(ref, payload, { merge: true });
      alert("✅ Timesheet saved!");
      setHasExisting(true);
    } catch (err) {
      console.error(err);
      alert("❌ Failed to save timesheet.");
    } finally {
      setSaving(false);
    }
  };

  // ──────────────────────────────────────────────
  // UI
  // ──────────────────────────────────────────────
  if (loading && !hasExisting) {
    return (
      <div
        style={{
          paddingTop: "100px",
          textAlign: "center",
          color: "white",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div style={{ paddingTop: "100px", background: "#0f172a", minHeight: "100vh" }}>
      <h1
        style={{
          textAlign: "center",
          color: "#fff",
          marginBottom: "20px",
          fontSize: "26px",
        }}
      >
        Manual Timesheet Creator
      </h1>

      {/* TOP CARD */}
      <div
        style={{
          background: "white",
          maxWidth: "900px",
          margin: "0 auto",
          padding: "25px",
          borderRadius: "16px",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
          {/* Staff */}
          <div style={{ flex: "1 1 240px" }}>
            <label>Staff Member</label>
            <select
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select staff</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.fullName} ({s.email})
                </option>
              ))}
            </select>
          </div>

          {/* Week Start */}
          <div style={{ flex: "1 1 160px" }}>
            <label>Week Start (Mon)</label>
            <input
              type="date"
              value={selectedWeekStart}
              onChange={(e) => setSelectedWeekStart(e.target.value)}
              style={inputStyle}
            />
            <p style={{ fontSize: "12px" }}>Week end: {weekEnd}</p>
          </div>

          {/* Status */}
          <div style={{ flex: "1 1 140px" }}>
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
              <option value="Pending">Pending</option>
              <option value="Submitted">Submitted</option>
              <option value="Approved">Approved</option>
            </select>
            <p style={{ fontSize: "12px", color: "#4b5563" }}>
              Staff page only shows Approved weeks.
            </p>
          </div>

          {/* TAX CODE */}
          <div style={{ flex: "1 1 160px" }}>
            <label>Tax Code</label>
            <select value={taxCode} onChange={(e) => setTaxCode(e.target.value)} style={inputStyle}>
              <option value="1257L">1257L</option>
              <option value="BR">BR</option>
              <option value="BR wk1mth1">BR wk1mth1</option>
              <option value="0T">0T</option>
              <option value="D0">D0</option>
              <option value="K">K Code</option>
            </select>
          </div>

          {/* NI CATEGORY */}
          <div style={{ flex: "1 1 160px" }}>
            <label>NI Category</label>
            <select
              value={niCategory}
              onChange={(e) => setNiCategory(e.target.value)}
              style={inputStyle}
            >
              <option value="A">A</option>
              <option value="C">C</option>
              <option value="H">H</option>
              <option value="M">M</option>
              <option value="Z">Z</option>
            </select>
          </div>

          {/* STUDENT LOAN */}
          <div style={{ flex: "1 1 160px" }}>
            <label>Student Loan</label>
            <select
              value={studentLoan}
              onChange={(e) => setStudentLoan(e.target.value)}
              style={inputStyle}
            >
              <option value="None">None</option>
              <option value="Plan1">Plan 1</option>
              <option value="Plan2">Plan 2</option>
              <option value="Plan4">Plan 4</option>
              <option value="PostGrad">Postgraduate</option>
            </select>
          </div>

          {/* SAVE BUTTON */}
          <div style={{ flex: "1 1 160px", display: "flex", alignItems: "flex-end" }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: saving ? "#94a3b8" : "#10b981",
                color: "white",
                padding: "12px 20px",
                borderRadius: "10px",
                border: "none",
                fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Saving..." : "Save Timesheet"}
            </button>
          </div>
        </div>

        {/* Totals */}
        <div style={{ marginTop: "10px", fontSize: "14px" }}>
          <strong>Total Hours:</strong> {totals.totalHours.toFixed(2)}  
          &nbsp;&nbsp;|&nbsp;&nbsp;
          <strong>Total Pay:</strong> {fmtGBP(totals.totalPay)}  
          &nbsp;&nbsp;|&nbsp;&nbsp;
          <strong>Approved Gross:</strong> {fmtGBP(totals.approvedGross)}  
          {hasExisting && (
            <span style={{ color: "#10b981", marginLeft: "6px" }}>
              (Existing timesheet loaded)
            </span>
          )}
        </div>
      </div>

      {/* ENTRIES TABLE */}
      <div
        style={{
          background: "white",
          maxWidth: "900px",
          margin: "25px auto",
          padding: "25px",
          borderRadius: "16px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h2>Timesheet Entries</h2>
          <button
            onClick={addEntry}
            style={{
              background: "#2563eb",
              color: "white",
              padding: "8px 14px",
              borderRadius: "8px",
              border: "none",
            }}
          >
            + Add Row
          </button>
        </div>

        {entries.length === 0 ? (
          <p style={{ color: "#6b7280", marginTop: "10px" }}>
            No entries yet. Click "Add Row" to begin.
          </p>
        ) : (
          <table
            style={{
              width: "100%",
              marginTop: "15px",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr style={{ background: "#e5e7eb" }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Hours</th>
                <th style={thStyle}>Rate</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Approved</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {entries.map((e, i) => {
                const amount = (e.hours || 0) * (e.rate || 0);
                return (
                  <tr key={i}>
                    {/* Date */}
                    <td style={tdStyle}>
                      <input
                        type="date"
                        value={e.date}
                        onChange={(ev) => updateEntry(i, "date", ev.target.value)}
                        style={rowInputStyle}
                      />
                    </td>

                    {/* Client */}
                    <td style={tdStyle}>
                      <select
                        value={e.clientName}
                        onChange={(ev) =>
                          updateEntry(i, "clientName", ev.target.value)
                        }
                        style={rowInputStyle}
                      >
                        <option value="">Select client</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.fullName}>
                            {c.fullName}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Hours */}
                    <td style={tdStyle}>
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        value={e.hours}
                        onChange={(ev) =>
                          updateEntry(i, "hours", Number(ev.target.value))
                        }
                        style={rowInputStyle}
                      />
                    </td>

                    {/* Rate */}
                    <td style={tdStyle}>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={e.rate}
                        onChange={(ev) =>
                          updateEntry(i, "rate", Number(ev.target.value))
                        }
                        style={rowInputStyle}
                      />
                    </td>

                    {/* Amount */}
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {fmtGBP(amount)}
                    </td>

                    {/* Approved */}
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={!!e.approved}
                        onChange={(ev) =>
                          updateEntry(i, "approved", ev.target.checked)
                        }
                      />
                    </td>

                    {/* Remove */}
                    <td style={tdStyle}>
                      <button
                        onClick={() => removeEntry(i)}
                        style={{
                          background: "#dc2626",
                          color: "white",
                          border: "none",
                          padding: "6px 10px",
                          borderRadius: "6px",
                        }}
                      >
                        🗑 Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* FOOTER TOTALS */}
            <tfoot>
              <tr>
                <td colSpan={2} style={{ padding: "8px", textAlign: "right" }}>
                  <strong>Totals:</strong>
                </td>
                <td style={{ padding: "8px" }}>
                  {totals.totalHours.toFixed(2)} h
                </td>
                <td />
                <td style={{ padding: "8px" }}>{fmtGBP(totals.totalPay)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

// Styles
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "10px",
  border: "1px solid #d1d5db",
  backgroundColor: "#f9fafb",
  fontSize: "14px",
};

const thStyle = {
  padding: "8px",
  textAlign: "left",
};

const tdStyle = {
  padding: "8px",
};

const rowInputStyle = {
  width: "100%",
  padding: "6px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
};
